import { useLocalStorage } from '@mantine/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { camelCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useMaybeAuthUser } from '../../api';
import { FEATURE_FLAGS_DEFAULT_VALUES } from './constants';
import { FeatureFlagsContext } from './FeatureFlagsContextStore';
import type { FeatureFlags } from './types';

export { FeatureFlagsContext };

export function FeatureFlagsProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const flags = useFlags();
	const { workspace } = useMaybeAuthUser();
	const [flagOverrides, setFlagOverrides] = useLocalStorage<
		Partial<FeatureFlags>
	>({
		key: 'feature-flags-override',
		defaultValue: {},
	});

	const featureFlags = useMemo(
		() =>
			Object.keys(FEATURE_FLAGS_DEFAULT_VALUES).reduce((acc, flagName) => {
				const camelCaseFlag = camelCase(flagName) as keyof FeatureFlags;

				// Priority order:
				// 1. Local storage overrides (highest - for development/testing)
				// 2. Workspace feature_flags_override (middle - admin controlled)
				// 3. LaunchDarkly flags (lowest - default)
				const workspaceOverride = workspace?.feature_flags_override?.[flagName];

				return {
					...acc,
					[camelCaseFlag]:
						flagOverrides[camelCaseFlag] ??
						workspaceOverride ??
						flags[camelCaseFlag] ??
						false,
				};
			}, {} as FeatureFlags),
		[flagOverrides, flags, workspace?.feature_flags_override]
	);

	const setOverride = useCallback(
		(flag: keyof FeatureFlags, value: boolean) => {
			setFlagOverrides((prev) => ({
				...prev,
				[flag]: value,
			}));
		},
		[setFlagOverrides]
	);

	const clearOverride = useCallback(
		(flag: keyof FeatureFlags) => {
			setFlagOverrides((prev) => ({
				...prev,
				[flag]: undefined,
			}));
		},
		[setFlagOverrides]
	);

	const contextValue = useMemo(
		() => ({
			featureFlags,
			setOverride,
			clearOverride,
			featureFlagsOverrides: flagOverrides,
			ldFeatureFlags: flags as FeatureFlags,
			isLoading: !Object.keys(flags ?? {}).length,
		}),
		[featureFlags, flagOverrides, setOverride, clearOverride, flags]
	);

	return (
		<FeatureFlagsContext.Provider value={contextValue}>
			{children}
		</FeatureFlagsContext.Provider>
	);
}
